import { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuLabel, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { LangToggle } from "@/components/common/LangToggle";
import { OpenAlertsBell } from "@/components/layout/OpenAlertsBell";
import { LayoutDashboard, Users, Building2, Package, Calculator, FileSpreadsheet, FileSignature, FolderKanban, ListChecks, LifeBuoy, LayoutTemplate, UserCog, History, Menu, LogOut, UserCircle, Home, FileText, ChevronDown, Receipt, Wallet, RefreshCw, Mail, BarChart3, BookOpenCheck, BellRing, HelpCircle, Cog } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { ROLE_LABELS } from "@/lib/format";

const INTERNAL = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/prospectos", label: "Prospectos", icon: Users },
  { to: "/clientes", label: "Clientes", icon: Building2 },
  { to: "/servicios", label: "Servicios y paquetes", icon: Package },
  { to: "/taxes", label: "Taxes", icon: Calculator },
  { to: "/cotizaciones", label: "Cotizaciones", icon: FileSpreadsheet },
  { to: "/propuestas", label: "Propuestas", icon: FileSignature },
  { to: "/proyectos", label: "Proyectos", icon: FolderKanban },
  { to: "/tareas", label: "Tareas", icon: ListChecks },
  { to: "/solicitudes", label: "Solicitudes", icon: LifeBuoy },
  { to: "/facturas", label: "Facturas", icon: Receipt },
  { to: "/pagos", label: "Pagos", icon: Wallet, admin: true },
  { to: "/renovaciones", label: "Renovaciones", icon: RefreshCw },
  { to: "/correos", label: "Correos", icon: Mail, admin: true },
  { to: "/reportes", label: "Reportes de ingresos", icon: BarChart3, admin: true },
  { to: "/documentacion", label: "Documento de entrega", icon: BookOpenCheck, admin: true },
  { to: "/plantillas", label: "Plantillas", icon: LayoutTemplate, admin: true },
  { to: "/usuarios", label: "Usuarios", icon: UserCog, admin: true },
  { to: "/actividad", label: "Historial de actividad", icon: History, admin: true },
  { to: "/ayuda", label: "Guía de prueba", icon: HelpCircle, admin: true },
  { to: "/configuracion", label: "Configuración", icon: Cog, admin: true },
];
const PORTAL = [
  { to: "/portal", label: "Inicio", icon: Home, end: true },
  { to: "/portal/propuestas", label: "Mis propuestas", icon: FileSignature },
  { to: "/portal/proyectos", label: "Mis proyectos", icon: FolderKanban },
  { to: "/portal/taxes", label: "Mis casos de taxes", icon: Calculator },
  { to: "/portal/documentos", label: "Documentos", icon: FileText },
  { to: "/portal/facturas", label: "Mis facturas", icon: Receipt },
  { to: "/portal/pagos", label: "Mis pagos", icon: Wallet },
  { to: "/portal/renovaciones", label: "Mis renovaciones", icon: RefreshCw },
  { to: "/portal/preferencias", label: "Preferencias de correo", icon: BellRing },
  { to: "/portal/solicitudes", label: "Solicitudes", icon: LifeBuoy },
];

function Nav({ items, onNavigate }) {
  return (
    <nav className="flex flex-col gap-1" aria-label="Navegación principal">
      {items.map((i) => (
        <NavLink key={i.to} to={i.to} end={i.end} onClick={onNavigate} className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`}
          data-testid={`nav-${i.to.replace(/\//g, "-").replace(/^-/, "") || "dashboard"}`}>
          <i.icon className="h-4 w-4 shrink-0" />{i.label}
        </NavLink>
      ))}
    </nav>
  );
}

function Brand({ portal }) {
  return (
    <div className="mb-8 flex items-center gap-2.5 px-2">
      <img src="/logo-icon.png" alt="RinoTECH" className="h-10 w-10 rounded-lg bg-white p-0.5" data-testid="sidebar-logo" />
      <div>
        <p className="font-display text-base font-extrabold leading-none text-white">RinoTECH</p>
        <p className="mt-1 text-[11px] uppercase tracking-widest text-slate-400">{portal ? "Portal del cliente" : "Gestión interna"}</p>
      </div>
    </div>
  );
}

export default function AppLayout({ portal }) {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const items = portal ? PORTAL : INTERNAL.filter((i) => !i.admin || user.role === "admin");
  const sidebar = (onNav) => (<div className="flex h-full flex-col bg-[#0F172A] p-4"><Brand portal={portal} /><Nav items={items} onNavigate={onNav} />
    <p className="mt-auto px-2 pt-6 text-[11px] text-slate-500">Zona horaria: America/Chicago · USD</p></div>);

  return (
    <div className="flex min-h-screen">
      <aside className="sticky top-0 hidden h-screen w-64 shrink-0 overflow-y-auto lg:block" data-testid="sidebar">{sidebar()}</aside>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="left" className="w-72 border-none p-0 [&>button]:text-white [&>button]:opacity-90">
          <SheetTitle className="sr-only">Menú</SheetTitle>
          {sidebar(() => setOpen(false))}
        </SheetContent>
      </Sheet>
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="no-print sticky top-0 z-30 flex h-16 items-center gap-3 border-b border-border bg-white/80 px-4 backdrop-blur-md sm:px-6">
          <button className="rounded-md p-2 hover:bg-slate-100 lg:hidden" onClick={() => setOpen(true)} aria-label="Abrir menú" data-testid="mobile-menu-button"><Menu className="h-5 w-5" /></button>
          <div className="flex-1" />
          <LangToggle saveToAccount />
          {!portal && <OpenAlertsBell />}
          <DropdownMenu>
            <DropdownMenuTrigger className="flex items-center gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-slate-100" data-testid="user-menu-button">
              <UserCircle className="h-6 w-6 text-slate-500" />
              <span className="hidden text-left sm:block"><span className="block font-semibold leading-tight text-slate-800">{user.name}</span>
                <span className="block text-xs text-slate-500">{ROLE_LABELS[user.role]}</span></span>
              <ChevronDown className="h-4 w-4 text-slate-400" />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56 bg-popover">
              <DropdownMenuLabel className="truncate text-xs text-slate-500">{user.email}</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => navigate(portal ? "/portal/perfil" : "/perfil")} data-testid="menu-profile">Mi perfil</DropdownMenuItem>
              <DropdownMenuItem onClick={async () => { await logout(); navigate("/login"); }} data-testid="menu-logout"><LogOut className="mr-2 h-4 w-4" />Cerrar sesión</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </header>
        <main className="mx-auto w-full max-w-[1400px] flex-1 p-4 sm:p-6 lg:p-8"><Outlet /></main>
      </div>
    </div>
  );
}
